import axios from 'axios';
import { getDeviceId } from '../utils/deviceId';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});
let authRedirectInProgress = false;

// Request interceptor to add token, handle FormData, and add plant filtering
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    const deviceId = getDeviceId();
    if (deviceId) {
      config.headers['X-Device-Id'] = deviceId;
    }

    // Auto-inject activePlant into GET request params for data filtering
    if (config.method?.toLowerCase() === 'get') {
      const activePlant = localStorage.getItem('activePlant');
      const activePlantId = localStorage.getItem('activePlantId');
      const activeLineId = localStorage.getItem('activeLineId');
      if (activePlant && activePlant !== 'All Units' && activePlant !== 'All') {
        config.params = { ...config.params };
        if (!config.params.unit && !config.params.plant && !config.params.plant_code) {
          config.params.plant = activePlant;
          config.params.plant_code = activePlant;
        }
      }
      config.params = { ...config.params };
      if (activePlantId && !config.params.plant_id) config.params.plant_id = activePlantId;
      if (activeLineId && !config.params.line_id) config.params.line_id = activeLineId;
    }
    
    // If request body is FormData, remove Content-Type header
    // to let the browser set it with the correct boundary
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const requestUrl = String(error?.config?.url || '');
    const isLocalAuthFailure =
      requestUrl.includes('/auth/login') ||
      requestUrl.includes('/auth/change-password') ||
      requestUrl.includes('/auth/reauth') ||
      requestUrl.includes('/auth/forgot-password') ||
      requestUrl.includes('/auth/reset-password');
    const skipAuthRedirect = Boolean(error?.config?.skipAuthRedirect);
    const status = Number(error?.response?.status || 0);
    const isAuthFailure = status === 401;

    // 402 + a code means the license gate blocked this request (missing/expired/
    // wrong-server key) — everyone but Super Admin gets sent to a full-page lock
    // screen instead of a broken module page. /auth and /license calls are exempt
    // on the backend already, but guard here too so this never loops on itself.
    const isLicenseBlock = status === 402 && !!error?.response?.data?.code && !requestUrl.includes('/license') && !requestUrl.includes('/auth');
    if (isLicenseBlock) {
      if (window.location.pathname !== '/license-locked') {
        sessionStorage.setItem('licenseBlockMessage', error.response.data.message || '');
        window.location.replace('/license-locked');
      }
      return new Promise(() => {});
    }

    // 403 + one of these codes means the network/device access-control gate
    // blocked this request (see services/accessControl.js) — send the user to
    // a full-page lock screen instead of a broken module page, same pattern
    // as the license gate above.
    const accessCode = error?.response?.data?.code;
    const isAccessBlock = status === 403 && ['NETWORK_NOT_ALLOWED', 'DEVICE_REVOKED'].includes(accessCode) && !requestUrl.includes('/auth');
    if (isAccessBlock) {
      if (window.location.pathname !== '/access-locked') {
        sessionStorage.setItem('accessBlockMessage', error.response.data.message || '');
        window.location.replace('/access-locked');
      }
      return new Promise(() => {});
    }

    if (isAuthFailure && !isLocalAuthFailure && !skipAuthRedirect) {
      error.__handledAuth = true;
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('lastActivityAt');
      if (!authRedirectInProgress) {
        authRedirectInProgress = true;
        window.location.replace('/login');
      }
      return new Promise(() => {});
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials, { skipAuthRedirect: true }),
  logout: () => api.post('/auth/logout'),
  getMe: () => api.get('/auth/me'),
  changePassword: (data) => api.post('/auth/change-password', data),
  reauth: (data) => api.post('/auth/reauth', data),
  forgotPassword: (data) => api.post('/auth/forgot-password', data),
  resetPassword: (data) => api.post('/auth/reset-password', data),
  // HttpOnly cookie that lets plain links/<img> open /uploads attachments.
  // withCredentials so the cookie is stored when the API is on another port (dev).
  startFileSession: () => api.post('/auth/file-session', null, { withCredentials: true, skipAuthRedirect: true }),
  endFileSession: () => api.post('/auth/file-session/end', null, { withCredentials: true, skipAuthRedirect: true }),
};

// Users API
export const usersAPI = {
  getAll: () => api.get('/users'),
  syncLmsDepartments: () => api.post('/users/sync-lms-departments'),
  create: (data, config) => api.post('/users', data, config),
  update: (id, data, config) => api.put(`/users/${id}`, data, config),
  delete: (id) => api.delete(`/users/${id}`),
};

// Departments API
export const departmentsAPI = {
  getAll: () => api.get('/departments'),
  create: (data) => api.post('/departments', data),
  update: (id, data) => api.put(`/departments/${id}`, data),
  delete: (id) => api.delete(`/departments/${id}`),
  getStagePermissions: () => api.get('/departments/stage-permissions'),
  setStagePermissions: (stageKey, departmentIds) => api.put(`/departments/stage-permissions/${stageKey}`, { department_ids: departmentIds }),
};

// Role Permissions API
export const rolePermissionsAPI = {
  getAll: () => api.get('/role-permissions'),
  setRoles: (permissionKey, roles) => api.put(`/role-permissions/${permissionKey}`, { roles }),
};

// Access Control (Network & Devices) API
export const accessControlAPI = {
  getSettings: () => api.get('/access-control/settings'),
  updateSettings: (settings) => api.put('/access-control/settings', settings),
  getDevices: (status) => api.get('/access-control/devices', { params: status ? { status } : {} }),
  approveDevice: (id) => api.post(`/access-control/devices/${id}/approve`),
  revokeDevice: (id) => api.post(`/access-control/devices/${id}/revoke`),
  forgetDevice: (id) => api.delete(`/access-control/devices/${id}`),
};

// Approval Chains API
export const approvalChainsAPI = {
  getAll: () => api.get('/approval-chains'),
  setChain: (workflowType, levels) => api.put(`/approval-chains/${workflowType}`, { levels }),
};

// Role Master API
export const rolesAPI = {
  getAll: () => api.get('/roles'),
  create: (roleName) => api.post('/roles', { role_name: roleName }),
  rename: (id, roleName) => api.put(`/roles/${id}`, { role_name: roleName }),
  deactivate: (id) => api.delete(`/roles/${id}`),
  setLevel: (id, level) => api.put(`/roles/${id}/level`, { level }),
};

// API Master
export const apiMasterAPI = {
  getAll: (params) => api.get('/api-master', { params }),
  create: (data) => api.post('/api-master', data),
  update: (id, data) => api.put(`/api-master/${id}`, data),
  delete: (id, data) => api.delete(`/api-master/${id}`, { data }),
  approve: (id, data) => api.put(`/api-master/${id}/approve`, data),
  removeFromLine: (id, data) => api.put(`/api-master/${id}/remove-line`, data),
  importRows: (data) => api.post('/api-master/import', data),
  getCleanabilityConfig: () => api.get('/api-master/cleanability-assessment/config'),
  getRevisions: (id) => api.get(`/api-master/${id}/revisions`),
};

// Products API
export const productsAPI = {
  getAll: (params) => api.get('/products', { params }),
  getById: (id) => api.get(`/products/${id}`),
  getOverview: (id) => api.get(`/products/${id}/overview`),
  getRevisions: (id) => api.get(`/products/${id}/revisions`),
  create: (data) => api.post('/products', data),
  update: (id, data) => api.put(`/products/${id}`, data),
  approveProduct: (id, data) => api.put(`/products/${id}/approve/product`, data),
  approveQa: (id, data) => api.put(`/products/${id}/approve/qa`, data),
  delete: (id, data) => api.delete(`/products/${id}`, { data }),
  bulkDelete: (productIds, signature) => api.post('/products/bulk-delete', { productIds, ...(signature || {}) }, { skipAuthRedirect: true }),
  bulkLinkTrain: (productIds, trainId, payload = {}) => api.post('/products/bulk-link-train', { productIds, trainId, ...(payload || {}) }),
  bulkApprove: (productIds, stage, approvalType, signature) => api.post('/products/bulk-approve', { productIds, stage, approval_type: approvalType, ...(signature || {}) }, { skipAuthRedirect: true }),
};

// Equipment API
export const equipmentAPI = {
  getAll: (params) => api.get('/equipment', { params }),
  getLibrary: () => api.get('/equipment/library'),
  create: (data) => api.post('/equipment', data),
  update: (id, data) => api.put(`/equipment/${id}`, data),
  delete: (id, esignData) => api.delete(`/equipment/${id}`, { data: esignData }),
  getDeletionImpact: (id) => api.get(`/equipment/${id}/deletion-impact`),
  bulkDelete: (equipmentIds, signature) => api.post('/equipment/bulk-delete', { equipmentIds, ...(signature || {}) }, { skipAuthRedirect: true }),
  bulkApprove: (equipmentIds, stage, approvalType, signature) => api.post('/equipment/bulk-approve', { equipmentIds, stage, approval_type: approvalType, ...(signature || {}) }, { skipAuthRedirect: true }),
  approve: (id, data) => api.put(`/equipment/${id}/approve`, data),
  resetSampleData: () => api.post('/equipment/admin/reset-sample'),
  getRevisions: (id) => api.get(`/equipment/${id}/revisions`),
  getTrainRevisions: (trainId) => api.get(`/equipment/trains/${trainId}/revisions`),
  
  // Equipment Parts
  getParts: () => api.get('/equipment/parts'),
  createPart: (data) => api.post('/equipment/parts', data),
  updatePart: (id, data) => api.put(`/equipment/parts/${id}`, data),
  deletePart: (id, esignData) => api.delete(`/equipment/parts/${id}`, { data: esignData }),
  
  // Equipment Trains
  getTrains: () => api.get('/equipment/trains'),
  getTrainsInline: () => api.get('/equipment-trains'),
  getTrainFull: (trainId) => api.get(`/equipment-trains/${trainId}/full`),
  createTrain: (data) => api.post('/equipment/trains', data),
  updateTrain: (id, data) => api.put(`/equipment/trains/${id}`, data),
  approveTrain: (id, data) => api.put(`/equipment/trains/${id}/approve`, data),
  bulkApproveTrain: (trainIds, payload) => api.post('/equipment/trains/bulk-approve', { trainIds, ...(payload || {}) }, { skipAuthRedirect: true }),
  deleteTrain: (id, esignData) => api.delete(`/equipment/trains/${id}`, { data: esignData }),
  getTrainDeletionImpact: (id) => api.get(`/equipment/trains/${id}/deletion-impact`),
  bulkDeleteTrain: (trainIds, signature) => api.post('/equipment/trains/bulk-delete', { trainIds, ...(signature || {}) }, { skipAuthRedirect: true }),
  
  // Train Members
  getTrainMembers: (trainId) => api.get(`/equipment/trains/${trainId}/members`),
  getEquipmentTrainLinks: (equipmentId) => api.get(`/equipment/${equipmentId}/train-links`),
  addTrainMember: (trainId, data) => api.post(`/equipment/trains/${trainId}/members`, data),
  updateTrainMember: (trainMemberId, data) => api.put(`/equipment/trains/members/${trainMemberId}`, data),
  removeTrainMember: (trainMemberId) => api.delete(`/equipment/trains/members/${trainMemberId}`),
};

// Units / Sections / Dosage Forms API
export const unitsAPI = {
  getAll: () => api.get('/units'),
  getById: (id) => api.get(`/units/${id}`),
  create: (data) => api.post('/units', data),
  update: (id, data) => api.put(`/units/${id}`, data),
  getProductionLines: (unitId) => api.get(`/units/${unitId}/production-lines`),
  createProductionLine: (unitId, data) => api.post(`/units/${unitId}/production-lines`, data),
  updateProductionLine: (unitId, lineId, data) => api.put(`/units/${unitId}/production-lines/${lineId}`, data),
  getDosageForms: (unitId) => api.get(`/units/${unitId}/dosage-forms`),
  createDosageForm: (unitId, data) => api.post(`/units/${unitId}/dosage-forms`, data),
  updateDosageForm: (unitId, formId, data) => api.put(`/units/${unitId}/dosage-forms/${formId}`, data),
};

// Cleaning Config API
export const cleaningConfigAPI = {
  getEquipment: (params) => api.get('/cleaning-config/equipment', { params }),
  getAll: (params) => api.get('/cleaning-config', { params }),
  create: (data) => api.post('/cleaning-config', data),
  update: (id, data) => api.put(`/cleaning-config/${id}`, data),
  delete: (id) => api.delete(`/cleaning-config/${id}`),
  uploadSop: (id, formData) => api.post(`/cleaning-config/${id}/sop`, formData),
  submit: (id, data) => api.post(`/cleaning-config/${id}/submit`, data),
  approve: (id, data) => api.post(`/cleaning-config/${id}/approve`, data),
  returnDraft: (id, data) => api.post(`/cleaning-config/${id}/return-draft`, data),
};

// Detergent Master API
export const detergentMasterAPI = {
  getAll: () => api.get('/detergent-master'),
  create: (data) => api.post('/detergent-master', data),
  update: (id, data) => api.put(`/detergent-master/${id}`, data),
  delete: (id) => api.delete(`/detergent-master/${id}`),
  importRows: (data) => api.post('/detergent-master/import', data),
};

// Analytical Methods API
export const analyticalMethodsAPI = {
  getAll: () => api.get('/analytical-methods'),
  create: (data) => api.post('/analytical-methods', data),
  update: (id, data) => api.put(`/analytical-methods/${id}`, data),
  // Recovery study endpoints
  getSurfaces: (params) => api.get('/analytical-methods/surfaces', { params }),
  getRecovery: (params) => api.get('/analytical-methods/recovery', { params }),
  getTrainArea: (trainId) => api.get(`/analytical-methods/train-area/${trainId}`),
  saveRecovery: (data) => api.post('/analytical-methods/recovery', data),
  submitRecovery: (id, data) => api.post(`/analytical-methods/recovery/${id}/submit`, data),
  approveRecovery: (id, data) => api.post(`/analytical-methods/recovery/${id}/approve`, data),
  uploadFile: (formData) => api.post('/analytical-methods/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteFile: (filename) => api.delete(`/analytical-methods/upload/${filename}`),
};

// Acceptance Criteria API
export const acceptanceCriteriaAPI = {
  getSolvents:   (params) => api.get('/acceptance-criteria/solvents', { params }),
  saveSolvent:   (data)   => api.post('/acceptance-criteria/solvents', data),
  deleteSolvent: (id)     => api.delete(`/acceptance-criteria/solvents/${id}`),
  getAreaClasses:   (params) => api.get('/acceptance-criteria/area-classes', { params }),
  saveAreaClass:    (data)   => api.post('/acceptance-criteria/area-classes', data),
  deleteAreaClass:  (id)     => api.delete(`/acceptance-criteria/area-classes/${id}`),
  getParts:      (params) => api.get('/acceptance-criteria/parts', { params }),
  getCriteria:   (params) => api.get('/acceptance-criteria/criteria', { params }),
  saveCriteria:  (data)   => api.post('/acceptance-criteria/criteria', data),
  submitCriteria:(id, data) => api.post(`/acceptance-criteria/criteria/${id}/submit`, data),
  approveCriteria:  (id, data) => api.post(`/acceptance-criteria/criteria/${id}/approve`, data),
  returnToDraft:    (id, data) => api.post(`/acceptance-criteria/criteria/${id}/return-to-draft`, data),
  deleteCriteria:   (id, data) => api.delete(`/acceptance-criteria/criteria/${id}`, { data }),
  getRevisions: (id) => api.get(`/acceptance-criteria/criteria/${id}/revisions`),
};

// VRL API
export const vrlAPI = {
  getAll: () => api.get('/vrl'),
  create: (data) => api.post('/vrl', data),
  update: (id, data) => api.put(`/vrl/${id}`, data),
  delete: (id) => api.delete(`/vrl/${id}`),
  approve: (id, data) => api.post(`/vrl/${id}/approve`, data),
  unapprove: (id, data) => api.post(`/vrl/${id}/unapprove`, data),
};

// Worst Case API
export const worstCaseAPI = {
  getAll: () => api.get('/worst-case'),
  calculate: (data) => api.post('/worst-case/calculate', data),
  finalize: (trainId, data) => api.post(`/worst-case/${trainId}/finalize`, data),
  getCurrentDecision: (trainId) => api.get(`/worst-case/${trainId}/current`),
  getDecisionHistory: (trainId) => api.get(`/worst-case/${trainId}/decisions`),
};

// MACO API
export const macoAPI = {
  getAll: () => api.get('/maco'),
  calculate: (data) => api.post('/maco/calculate', data),
  getGoverningMar: (params) => api.get('/maco/governing-mar', { params }),
  previewGoverningMar: (params) => api.get('/maco/governing-mar', { params: { ...params, include_draft: true } }),
  getMatrix: (trainId) => api.get(`/maco/matrix/${trainId}`),
  getAutoWorstCase: (trainId) => api.get(`/maco/${trainId}/auto-worst-case`),
  finalizeGoverning: (trainId, data) => api.post(`/maco/${trainId}/finalize-governing`, data),
  getCurrentGoverning: (trainId) => api.get(`/maco/${trainId}/current-governing`),
  getGoverningHistory: (trainId) => api.get(`/maco/${trainId}/governing-history`),
};

// Sampling Plans API
export const samplingPlansAPI = {
  getAll: () => api.get('/sampling-plans'),
  getById: (id) => api.get(`/sampling-plans/${id}`),
  create: (data) => api.post('/sampling-plans', data),
  update: (id, data) => api.put(`/sampling-plans/${id}`, data),
  delete: (id, data) => api.delete(`/sampling-plans/${id}`, { data }),
  submit: (id, data) => api.post(`/sampling-plans/${id}/submit`, data),
  approve: (id, data) => api.post(`/sampling-plans/${id}/approve`, data),
  returnToDraft: (id, data) => api.post(`/sampling-plans/${id}/return-to-draft`, data),
  getRevisions: (id) => api.get(`/sampling-plans/${id}/revisions`),
};

// Results API
export const resultsAPI = {
  getAll: () => api.get('/results'),
  create: (data) => api.post('/results', data),
  signCleaning: (id, data) => api.put(`/results/${id}/sign-cleaning`, data),
  signSampling: (id, data) => api.put(`/results/${id}/sign-sampling`, data),
  samplingIntimation: (id, data) => api.put(`/results/${id}/sampling-intimation`, data),
  signVisual: (id, data) => api.put(`/results/${id}/sign-visual`, data),
  analyze: (id, data) => api.put(`/results/${id}/analyze`, data),
  approve: (id, data) => api.put(`/results/${id}/approve`, data),
  getRevisions: (id) => api.get(`/results/${id}/revisions`),
};

// Deviations / OOS API
export const deviationsAPI = {
  getAll: (params) => api.get('/deviations', { params }),
  update: (id, data) => api.put(`/deviations/${id}`, data),
};

// Reports API (in-app expandable study report)
export const reportsAPI = {
  getStudy: (planId) => api.get(`/reports/study/${planId}`),
};

// Training API
export const trainingAPI = {
  getAll: () => api.get('/training'),
  create: (data) => api.post('/training', data),
  getRecords: () => api.get('/training/records'),
  createRecord: (data) => api.post('/training/records', data),
  approveRecord: (id, data) => api.post(`/training/records/${id}/approve`, data),
  approveCourse: (id, data) => api.post(`/training/${id}/approve`, data),
  syncLmsEmployees: () => api.post('/training/sync-lms-employees'),
  importRows: (data) => api.post('/training/import', data),
  getComplianceMatrix: () => api.get('/training/compliance-matrix'),
  getUserCompliance: (userId) => api.get(`/training/compliance/${userId}`),
};

// CCV API
export const ccvAPI = {
  getAll: () => api.get('/ccv'),
  create: (data) => api.post('/ccv', data),
  update: (id, data) => api.put(`/ccv/${id}`, data),
  submit: (id, data) => api.post(`/ccv/${id}/submit`, data),
  approve: (id, data) => api.post(`/ccv/${id}/approve`, data),
  returnToDraft: (id, data) => api.post(`/ccv/${id}/return-to-draft`, data),
  delete: (id, data) => api.delete(`/ccv/${id}`, { data }),

  getResults: () => api.get('/ccv/results'),
  createResult: (data) => api.post('/ccv/results', data),
  updateResult: (id, data) => api.put(`/ccv/results/${id}`, data),
  submitResult: (id, data) => api.post(`/ccv/results/${id}/submit`, data),
  approveResult: (id, data) => api.post(`/ccv/results/${id}/approve`, data),
  returnResultToDraft: (id, data) => api.post(`/ccv/results/${id}/return-to-draft`, data),
  deleteResult: (id, data) => api.delete(`/ccv/results/${id}`, { data }),
};

// Revalidation API
export const revalidationAPI = {
  getAll: () => api.get('/revalidation'),
  create: (data) => api.post('/revalidation', data),
  decide: (id, data) => api.put(`/revalidation/${id}/decide`, data),
  linkPlan: (id, data) => api.post(`/revalidation/${id}/link-plan`, data),
  close: (id, data) => api.post(`/revalidation/${id}/close`, data),
  approve: (id) => api.put(`/revalidation/${id}/approve`),
};

// System Change Control API
export const changeControlAPI = {
  getAll: () => api.get('/change-control'),
  get: (id) => api.get(`/change-control/${id}`),
  create: (data) => api.post('/change-control', data),
  update: (id, data) => api.put(`/change-control/${id}`, data),
  remove: (id) => api.delete(`/change-control/${id}`),
  submit: (id) => api.post(`/change-control/${id}/submit`),
  decide: (id, data) => api.post(`/change-control/${id}/approve`, data),
  returnToDraft: (id, data) => api.post(`/change-control/${id}/return-to-draft`, data),
};

// Dashboard API
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
  getPendingApprovals: () => api.get('/dashboard/pending-approvals'),
  getMyWork: () => api.get('/dashboard/my-work'),
  getRecentActivities: () => api.get('/dashboard/recent-activities'),
  getAnalytics: (range) => api.get('/dashboard/analytics', { params: range ? { range } : {} }),
  getStatRecords: (key) => api.get(`/dashboard/stats/${key}/records`),
  getAnalyticsRecords: (chart, key) => api.get(`/dashboard/analytics/${chart}/${encodeURIComponent(key)}/records`),
};

// Audit API
export const auditAPI = {
  getTrail: (params) => api.get('/audit', { params }),
  getActions: () => api.get('/audit/actions'),
  getSummary: () => api.get('/audit/summary'),
  verify: () => api.get('/audit/verify'),
  logExport: (payload) => api.post('/audit/export-log', payload),
};

// Documents API
export const documentsAPI = {
  getAll: () => api.get('/documents'),
  create: (data) => api.post('/documents', data),
};

// System Settings API
export const settingsAPI = {
  getAll: () => api.get('/settings'),
  getSetting: (key) => api.get(`/settings/${key}`),
  updateSetting: (key, value) => api.put(`/settings/${key}`, { value }),
  getSignatures: (apiId) => api.get(`/settings/api-master/signatures/${apiId}`),
  createSignature: (data) => api.post('/settings/signatures', data),
  nextSequence: (counterKey) => api.post(`/settings/next-sequence/${counterKey}`),
};

// Backup & Restore API (Admin/Super Admin — see backend/routes/backup.js)
export const backupAPI = {
  list: () => api.get('/backups'),
  getJob: () => api.get('/backups/job'),
  saveSettings: (data) => api.put('/backups/settings', data),
  create: (data) => api.post('/backups', data),
  upload: (formData, onUploadProgress) => api.post('/backups/upload', formData, { onUploadProgress }),
  downloadToken: (id) => api.post(`/backups/${id}/download-token`),
  restore: (id, data) => api.post(`/backups/${id}/restore`, data),
  deleteRequest: (id, reason) => api.post(`/backups/${id}/delete-request`, { reason }),
  rejectDeleteRequest: (id) => api.post(`/backups/${id}/delete-request/reject`),
  remove: (id) => api.delete(`/backups/${id}`),
};

// Commercial Setup API
export const commercialConfigAPI = {
  getRuntime: () => api.get('/commercial-config/runtime'),
  getFull: () => api.get('/commercial-config/full'),
  saveFull: (data) => api.put('/commercial-config/full', data),
  getSite: () => api.get('/commercial-config/site'),
  saveSite: (data) => api.put('/commercial-config/site', data),
  hardResetPreview: () => api.get('/commercial-config/hard-reset/preview'),
  hardResetStatus: () => api.get('/commercial-config/hard-reset/status'),
  hardReset: (data) => api.post('/commercial-config/hard-reset', data)
};

// Cleaning-validation Protocols & Reports (fixed template, approval, effective, LMS)
export const cvDocumentsAPI = {
  list: () => api.get('/cv-documents'),
  get: (id) => api.get(`/cv-documents/${id}`),
  preview: (planId, type) => api.get(`/cv-documents/preview/${planId}`, { params: { type } }),
  create: (data) => api.post('/cv-documents', data),
  update: (id, data) => api.put(`/cv-documents/${id}`, data),
  submit: (id, data) => api.post(`/cv-documents/${id}/submit`, data),
  decide: (id, data) => api.post(`/cv-documents/${id}/decision`, data),
  makeEffective: (id, data) => api.post(`/cv-documents/${id}/make-effective`, data),
  revise: (id, data) => api.post(`/cv-documents/${id}/revise`, data),
  sendToLms: (id) => api.post(`/cv-documents/${id}/send-to-lms`),
  pullTraining: (id) => api.post(`/cv-documents/${id}/pull-training`),
  sendToDms: (id) => api.post(`/cv-documents/${id}/send-to-dms`),
};

// Optional app integrations (AiventraLMS, AiventraQMS, SAP, other REST apps)
export const integrationsAPI = {
  list: () => api.get('/integrations'),
  save: (key, data) => api.put(`/integrations/${key}`, data),
  test: (key) => api.post(`/integrations/${key}/test`),
  syncEmployees: (key) => api.post(`/integrations/${key}/sync-employees`),
  syncDepartments: (key) => api.post(`/integrations/${key}/sync-departments`),
  employeeMatches: (key) => api.get(`/integrations/${key}/employee-matches`),
  linkEmployees: (key, data) => api.post(`/integrations/${key}/link-employees`, data),
  pull: (key, kind) => api.get(`/integrations/${key}/pull/${kind}`),
  logs: (key, limit) => api.get('/integrations/logs', { params: { key, limit } }),
  linkedStatus: () => api.get('/integrations/linked/status'),
  runLinked: () => api.post('/integrations/linked/run'),
  retryDeviations: () => api.post('/integrations/aiventra_qms/retry-deviations'),
};

// License key provisioning/activation (signed, server-locked keys)
export const licenseAPI = {
  get: () => api.get('/license'),
  getSigningKey: () => api.get('/license/signing-key'),
  activate: (licenseKey, clientName) => api.post('/license/activate', { license_key: licenseKey, client_name: clientName }),
  // Sign-in screen (no session): is this installation licensed, and activate a key before anyone signs in.
  publicStatus: () => api.get('/license/public-status', { skipAuthRedirect: true }),
  activateInitial: (licenseKey) => api.post('/license/activate-initial', { license_key: licenseKey }, { skipAuthRedirect: true }),
  mint: (data) => api.post('/license/mint', data),
};

// Risk Assessment API (Cleaning Validation Risk Ranking)
export const riskAssessmentAPI = {
  getAll: (params) => api.get('/risk-assessment', { params }),
  getById: (id) => api.get(`/risk-assessment/${id}`),
  create: (data) => api.post('/risk-assessment', data),
  update: (id, data) => api.put(`/risk-assessment/${id}`, data),
  delete: (id, data) => api.delete(`/risk-assessment/${id}`, { data }),
  calculate: (data) => api.post('/risk-assessment/calculate', data),
  generate: (productId) => api.post(`/risk-assessment/generate/${productId}`),
  submit: (id, data) => api.post(`/risk-assessment/${id}/submit`, data),
  approve: (id, data) => api.post(`/risk-assessment/${id}/approve`, data),
  returnToDraft: (id, data) => api.post(`/risk-assessment/${id}/return-to-draft`, data),
  uploadEvidence: (formData) => api.post('/risk-assessment/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  getRevisions: (id) => api.get(`/risk-assessment/${id}/revisions`),
};

// Impact Assessment API — screens a new/edited product for CV (cleaning validation) impact:
// does it change the worst-case product/API or MACO basis on its train(s), or touch equipment
// with no validated cleaning method on file.
export const impactAssessmentAPI = {
  getAll: (params) => api.get('/impact-assessment', { params }),
  getByProductId: (productId) => api.get(`/impact-assessment/${productId}`),
  evaluate: (data) => api.post('/impact-assessment/evaluate', data),
  update: (id, data) => api.put(`/impact-assessment/${id}`, data),
  submit: (id, data) => api.post(`/impact-assessment/${id}/submit`, data),
  decide: (id, data) => api.post(`/impact-assessment/${id}/decision`, data),
  getRevisions: (id) => api.get(`/impact-assessment/${id}/revisions`),
};

// AI Providers API (Super Admin — manage OpenRouter/Gemini/DeepSeek/self-hosted connections)
export const aiProvidersAPI = {
  getPresets: () => api.get('/ai-providers/presets'),
  getAll: () => api.get('/ai-providers'),
  create: (data) => api.post('/ai-providers', data),
  update: (id, data) => api.put(`/ai-providers/${id}`, data),
  delete: (id) => api.delete(`/ai-providers/${id}`),
  setDefault: (id) => api.post(`/ai-providers/${id}/set-default`),
  test: (id) => api.post(`/ai-providers/${id}/test`),
};

// Approval Flow — level-wise approvers per activity (Settings → Approval Flow)
export const approvalFlowAPI = {
  getAll: () => api.get('/approval-flow'),
  save: (workflowType, data) => api.put(`/approval-flow/${workflowType}`, data),
  getState: (workflowType, entityId) => api.get(`/approval-flow/state/${workflowType}/${entityId}`),
  getUserEligibility: (userId) => api.get(`/approval-flow/eligibility/${userId}`),
  getEligibilityMatrix: () => api.get('/approval-flow/eligibility-matrix'),
};

// Alerts & Notifications — Email / SMS / WhatsApp / Telegram task alerts with editable templates
export const notificationsAPI = {
  getSettings: () => api.get('/notifications/settings'),
  saveGeneral: (data) => api.put('/notifications/general', data),
  saveChannel: (channel, data) => api.put(`/notifications/channels/${channel}`, data),
  testChannel: (channel, data) => api.post(`/notifications/channels/${channel}/test`, data),
  saveEvent: (eventKey, data) => api.put(`/notifications/events/${eventKey}`, data),
  getTemplates: () => api.get('/notifications/templates'),
  saveTemplate: (eventKey, channel, data) => api.put(`/notifications/templates/${eventKey}/${channel}`, data),
  resetTemplate: (eventKey, channel) => api.post(`/notifications/templates/${eventKey}/${channel}/reset`),
  getLog: (limit) => api.get('/notifications/log', { params: { limit } }),
  getUserPrefs: (userId) => api.get(`/notifications/user-prefs/${userId}`),
  saveUserPrefs: (userId, data) => api.put(`/notifications/user-prefs/${userId}`, data),
};

// AI Assistant API — chat with app data, protocol/report drafting, risk/data review, grouping suggestions
export const aiAPI = {
  chat: (message, history) => api.post('/ai/chat', { message, history }),
  draftReport: (data) => api.post('/ai/draft-report', data),
  reviewRisk: (riskAssessmentId) => api.post('/ai/review-risk', { riskAssessmentId }),
  reviewApi: (apiId) => api.post('/ai/review-api', { apiId }),
  reviewData: (scope, instructions) => api.post('/ai/review-data', { scope, instructions }),
  suggestGrouping: (type, candidateIds) => api.post('/ai/suggest-grouping', { type, candidateIds }),
};

export default api;


